import React, { useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { message } from "antd";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CloseIcon from "@mui/icons-material/Close";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import HomeIcon from "@mui/icons-material/Home";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import PublicIcon from "@mui/icons-material/Public";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

import "./SideMenu.css";

const SideMenu = ({ sideMenu, setSideMenu }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.user);

  const closeMenu = () => setSideMenu(false);

  const handleLogout = () => {
    localStorage.clear();
    message.success("Logout Successful");
    closeMenu();
    navigate("/login");
  };

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path;
  };

  const accountPath = user?.isAdmin ? "/admin-dashboard" : user ? "/user-dashboard" : "/login";

  const displayName = useMemo(() => {
    if (!user) return "Guest Player";
    const name = String(user?.fname || user?.name || "").trim();
    if (name) return name;

    const email = String(user?.email || "").trim();
    return email.includes("@") ? email.split("@")[0] : email || "My Account";
  }, [user]);

  const balanceText = useMemo(() => {
    const raw = user?.balance;
    const num = typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw) : 0;
    const balance = Number.isFinite(num) && num >= 0 ? num : 0;
    return balance.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }, [user]);

  const userInitial = useMemo(() => {
    const initial = displayName.trim().charAt(0);
    return initial ? initial.toUpperCase() : "L";
  }, [displayName]);

  const menuItems = [
    { label: "Home", path: "/", icon: <HomeIcon /> },
    { label: "Games", path: "/games", icon: <SportsEsportsIcon /> },
    { label: "Leaderboard", path: "/leaderboard", icon: <EmojiEventsIcon /> },
    { label: "Check Region", path: "/name-check", icon: <PublicIcon /> },
    { label: user ? "Dashboard" : "My Account", path: accountPath, icon: user ? <PersonIcon /> : <LoginIcon /> },
  ];

  const userItems = user
    ? [
        { label: "Wallet", path: "/add-balance", icon: <AccountBalanceWalletIcon /> },
        { label: "Order History", path: "/orders", icon: <ReceiptLongIcon /> },
        { label: "Reseller Subscription", path: "/subscription", icon: <WorkspacePremiumIcon /> },
      ]
    : [];

  return (
    <div className={`sidemenu-container d-block d-md-block d-lg-none ${sideMenu ? "active" : ""}`}>
      <aside className="sidemenu" aria-label="Side menu">
        <button type="button" className="closeBtn" aria-label="Close menu" onClick={closeMenu}>
          <CloseIcon className="closeIcon" />
        </button>

        <div className="sidemenu-header">
          <Link to="/" onClick={closeMenu} className="sideBrand">
            <span className="sideBrandMark">LS</span>
            <span className="sideBrandCopy">
              <span className="sideBrandName">LIMPOOK</span>
              <span className="sideBrandSub">Official Store</span>
            </span>
          </Link>

          <div className={`sideUserCard ${user ? "isLogged" : "isGuest"}`}>
            <Link to={accountPath} onClick={closeMenu} className="sideUserMain">
              <span className="sideUserAvatar">{user ? userInitial : <PersonIcon />}</span>
              <span className="sideUserCopy">
                <span className="sideUserLabel">{user ? "Signed in" : "Welcome back"}</span>
                <strong>{displayName}</strong>
              </span>
            </Link>

            {user ? (
              <Link to="/add-balance" onClick={closeMenu} className="sideWalletChip">
                <CurrencyRupeeIcon />
                <span>{balanceText}</span>
              </Link>
            ) : (
              <Link to="/login" onClick={closeMenu} className="sideWalletChip">
                <LoginIcon />
                <span>Login</span>
              </Link>
            )}
          </div>
        </div>

        <div className="sideSectionLabel">Navigation</div>
        <ul className="menuList">
          {menuItems.map((item) => (
            <li key={item.label} className={`menuItem ${isActive(item.path) ? "active" : ""}`}>
              <Link to={item.path} className="menuLink" onClick={item.onClick || closeMenu}>
                <span className="menuIcon">{item.icon}</span>
                <span className="menuText">{item.label}</span>
              </Link>
            </li>
          ))}
        </ul>

        {user && (
          <Link to="/subscription" className="sideResellerBanner" onClick={closeMenu}>
            <span className="sideResellerBannerIcon"><WorkspacePremiumIcon /></span>
            <span className="sideResellerBannerCopy">
              <strong>Reseller Subscription</strong>
              <small>Unlock discounted pricing</small>
            </span>
            <span className="sideResellerBannerArrow">›</span>
          </Link>
        )}

        {userItems.length > 0 && (
          <>
            <div className="sideSectionLabel sideSectionLabelAccount">Account</div>
            <ul className="menuList">
              {userItems.map((item) => (
                <li key={item.label} className={`menuItem ${isActive(item.path) ? "active" : ""}`}>
                  <Link to={item.path} className="menuLink" onClick={closeMenu}>
                    <span className="menuIcon">{item.icon}</span>
                    <span className="menuText">{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}

        <div className="menuBottom">
          {user ? (
            <button type="button" className="logoutBtn" onClick={handleLogout}>
              <span className="logoutIconWrap">
                <LogoutIcon />
              </span>
              <span className="logoutText">Logout</span>
            </button>
          ) : (
            <Link to="/login" onClick={closeMenu} className="loginBtn">
              <span className="loginIconWrap">
                <LoginIcon />
              </span>
              <span>Login</span>
            </Link>
          )}
        </div>
      </aside>
    </div>
  );
};

export default SideMenu;
